import * as THREE from "three";

import { formatNumber, indicatorColors, normalizeValue, rampFor } from "./indicatorEngine";

/**
 * Couche "debit" : stations hydrometriques DRIAS-EAU, regime des cours d'eau
 * et etiages, de l'actuel a 2100.
 *
 * POURQUOI DES MARQUEURS ET PAS UNE NAPPE, contrairement au climat : un
 * debit est la propriete d'un cours d'eau en un point, pas d'un champ
 * continu sur le territoire. Interpoler entre deux stations de bassins
 * versants differents produirait une belle nappe qui ne voudrait rien dire.
 *
 * CE QUE CETTE COUCHE APPORTE. Les autres couches decrivent ce qui tombe
 * (climat) ou ce qui est preleve (prelevements) ; celle-ci dit ce qu'il
 * reste dans la riviere, et c'est la seule qui aille jusqu'a 2100. La
 * grandeur qui parle est le nombre de jours par an passes sous le QMNA5,
 * le debit d'etiage de reference des SDAGE : le seuil est cale sur l'horizon
 * actuel et FIGE aux quatre horizons, donc son evolution traduit bien la
 * baisse de debit et non un changement de reference.
 *
 * Comme pour le climat, les grandeurs affichables sont declarees par le
 * pipeline (`axes.metrics` de discharge.json, cf. build_overlays.py) : le
 * front ne connait aucun chemin de donnee, il rend une description.
 */

// Decollage des marqueurs et taille relative. Les stations de debit sont peu
// nombreuses (une dizaine par departement) mais doivent rester distinguables
// des points de mesure climatiques, bien plus denses : marqueur plus haut et
// plus etroit, silhouette de jauge plutot que de pastille.
const MARKER_RADIUS_RATIO = 0.0022;
const MARKER_HEIGHT_RATIO = 0.16;
const MARKER_MIN_HEIGHT = 260;
const HIT_RADIUS_RATIO = 4;

export const DISCHARGE_MONTH_LABELS = [
  "Jan", "Fév", "Mar", "Avr", "Mai", "Juin", "Juil", "Août", "Sep", "Oct", "Nov", "Déc",
];

const NO_VALUE_COLOR = new THREE.Color("#9a9a9a");

export function parseDischargeAxes(discharge) {
  const axes = discharge?.axes;
  if (!axes) return { horizons: [], metrics: [] };
  return {
    horizons: (axes.horizons || []).map((h) => (typeof h === "string" ? h : h.id)),
    horizonYears: Object.fromEntries(
      (axes.horizons || []).filter((h) => typeof h !== "string").map((h) => [h.id, h.annee])
    ),
    metrics: axes.metrics || [],
  };
}

export function horizonLabel(horizon) {
  return horizon === "actuel" ? "Actuel" : horizon;
}

/** Valeur d'un chemin pointe dans l'horizon d'une station, ou null. */
export function dischargeValueAt(station, metric, horizon) {
  let current = station?.horizons?.[horizon];
  if (!current || !metric) return null;
  for (const key of metric.id.split(".")) {
    if (current == null || typeof current !== "object") return null;
    current = current[key];
  }
  return typeof current === "number" && Number.isFinite(current) ? current : null;
}

export function formatDischargeValue(metric, value) {
  if (typeof value !== "number" || !Number.isFinite(value)) return "n/d";
  const formatted = formatNumber(value, metric.format);
  return metric.unit ? `${formatted} ${metric.unit}` : formatted;
}

/**
 * Une couleur par station pour la grandeur et l'horizon affiches.
 *
 * Passe par indicatorColors : les specifications emises par le pipeline ont
 * la meme forme que celles des communes ou de la geologie, donc la meme
 * mecanique de rampe et de normalisation s'applique — y compris l'echelle
 * logarithmique, indispensable ici (les modules vont de 0,3 a 50 m³/s sur un
 * meme departement).
 */
export function dischargeColors(stations, metric, horizon) {
  if (!metric) return (stations || []).map(() => NO_VALUE_COLOR);
  const records = (stations || []).map((station) => ({
    [metric.id]: dischargeValueAt(station, metric, horizon),
  }));
  return indicatorColors(records, metric, null);
}

/**
 * Marqueurs des stations : un InstancedMesh double d'un mesh de clic
 * invisible plus large — un marqueur de quelques centaines de metres est
 * invisable a l'echelle d'un departement. Meme schema que les points
 * climatiques et les prelevements.
 */
export function createDischargeStations({ stations, sampleAt, worldDiag, zRange, exaggeration }) {
  const placed = [];
  (stations || []).forEach((station) => {
    const sample = sampleAt(station.x, station.y);
    if (sample) placed.push({ station, sample });
  });
  if (!placed.length) return null;

  const radius = Math.max(worldDiag * MARKER_RADIUS_RATIO, 240);
  const height = Math.max(zRange * MARKER_HEIGHT_RATIO, MARKER_MIN_HEIGHT);
  const hitRadius = Math.max(radius * HIT_RADIUS_RATIO, worldDiag * 0.0012);

  // Prisme octogonal a section constante : lu de loin, il se distingue des
  // cones du climat sans avoir besoin d'une legende de formes.
  const geometry = new THREE.CylinderGeometry(1, 1, 1, 8);
  const mesh = new THREE.InstancedMesh(
    geometry,
    new THREE.MeshStandardMaterial({ roughness: 0.35, metalness: 0.05 }),
    placed.length
  );
  mesh.castShadow = true;
  const hitMesh = new THREE.InstancedMesh(
    geometry,
    new THREE.MeshBasicMaterial({ visible: false }),
    placed.length
  );

  const dummy = new THREE.Object3D();
  const meta = placed.map(({ station, sample }, index) => ({
    index,
    data: station,
    x: sample.x,
    z: sample.z,
    rawH: sample.rawH,
    radius,
    height,
    hitRadius,
    baseColor: new THREE.Color("#ffffff"),
  }));
  meta.forEach((entry) => {
    applyDischargeStationTransform(mesh, dummy, entry, exaggeration, entry.radius);
    applyDischargeStationTransform(hitMesh, dummy, entry, exaggeration, entry.hitRadius);
  });
  mesh.instanceMatrix.needsUpdate = true;
  hitMesh.instanceMatrix.needsUpdate = true;

  return { mesh, hitMesh, meta, dummy };
}

export function applyDischargeStationTransform(mesh, dummy, station, exaggeration, radius) {
  station.currentY = station.rawH * exaggeration;
  dummy.position.set(station.x, station.currentY + station.height / 2, station.z);
  dummy.scale.set(radius, station.height, radius);
  dummy.updateMatrix();
  mesh.setMatrixAt(station.index, dummy.matrix);
}

/**
 * Recolore les marqueurs quand la grandeur ou l'horizon change, et fait
 * varier leur HAUTEUR avec la valeur : sur une dizaine de marqueurs, la
 * hauteur se compare d'un coup d'oeil la ou une nuance de couleur demande de
 * revenir a la legende.
 */
export function paintDischargeStations(stations, metric, horizon) {
  if (!stations) return;
  const ramp = rampFor(metric);
  stations.meta.forEach((entry) => {
    const value = metric ? dischargeValueAt(entry.data, metric, horizon) : null;
    if (typeof value === "number") {
      const t = Math.min(1, Math.max(0, normalizeValue(value, metric)));
      const [r, g, b] = sampleRamp(t, ramp);
      entry.baseColor = new THREE.Color().setRGB(r / 255, g / 255, b / 255, THREE.SRGBColorSpace);
      // Plancher a 35 % : un marqueur ecrase au sol deviendrait invisible et
      // laisserait croire a une station absente plutot qu'a une valeur basse.
      entry.valueRatio = 0.35 + 0.65 * t;
    } else {
      entry.baseColor = NO_VALUE_COLOR;
      entry.valueRatio = 0.35;
    }
    stations.mesh.setColorAt(entry.index, entry.baseColor);
  });
  if (stations.mesh.instanceColor) stations.mesh.instanceColor.needsUpdate = true;
}

function sampleRamp(t, ramp) {
  const clamped = Math.min(1, Math.max(0, t));
  for (let i = 0; i < ramp.length - 1; i++) {
    const [t0, c0] = ramp[i];
    const [t1, c1] = ramp[i + 1];
    if (clamped >= t0 && clamped <= t1) {
      const f = (clamped - t0) / (t1 - t0 || 1);
      return [c0[0] + (c1[0] - c0[0]) * f, c0[1] + (c1[1] - c0[1]) * f, c0[2] + (c1[2] - c0[2]) * f];
    }
  }
  return ramp[ramp.length - 1][1];
}

/**
 * Profil mensuel d'une station, un jeu par horizon (graphe de la fiche).
 *
 * `coefficients_parde` (debit du mois divise par le module) plutot que les
 * m³/s bruts quand on veut comparer des stations entre elles : au-dessus de
 * 1 c'est la periode de hautes eaux, en dessous les basses eaux, et deux
 * rivieres de tailles tres differentes deviennent comparables.
 */
export function stationMonthlyDischarge(station, horizons, { normalized = false } = {}) {
  const field = normalized ? "coefficients_parde" : "mensuel_m3s";
  return (horizons || []).map((horizon) => {
    const data = station?.horizons?.[horizon];
    return {
      horizon,
      values: Array.isArray(data?.[field]) ? data[field] : DISCHARGE_MONTH_LABELS.map(() => null),
      module: data?.module_m3s ?? null,
    };
  });
}
